import type { CombatRatings } from "@/game/model/character-stats";
import { monsterCombatFromPart } from "@/game/model/monster-stats";
import { normalizePartKind, placementDepth } from "@/home/part-kind";
import type { HomePartDefinition } from "@/home/storage";
import { resolveTowerArenaAmbience } from "@/tower/tower-arena-ambience";
import type { TowerEnemySpawn, TowerFloorBattlePlan, TowerScenePiece } from "@/tower/tower-floor-runtime";
import { TOWER_DEFAULT_SPAWN_PER_MINUTE } from "@/tower/tower-spawn-rate";
import type { DungeonFloorPublishSnapshot } from "@/dungeon/dungeon-publish";

function scaleMonsterCombat(
  combat: { maxHp: number; xpReward: number; ratings: CombatRatings },
  multiplier: number,
) {
  const m = multiplier > 0 ? multiplier : 1;
  const ratings: CombatRatings = {
    ...combat.ratings,
    attack: Math.max(1, Math.floor(combat.ratings.attack * m)),
    defense: Math.max(0, Math.floor(combat.ratings.defense * m)),
    dodge: Math.max(0, Math.floor(combat.ratings.dodge * m)),
    hit: Math.max(1, Math.floor(combat.ratings.hit * m)),
  };
  return {
    maxHp: Math.max(1, Math.floor(combat.maxHp * m)),
    xpReward: combat.xpReward,
    ratings,
  };
}

function partById(parts: HomePartDefinition[], id: string): HomePartDefinition | undefined {
  return parts.find((p) => p.id === id);
}

function buildScenePieces(
  placements: { partId: string; gridX: number; gridY: number; depthBias?: number }[],
  sceneParts: HomePartDefinition[],
): TowerScenePiece[] {
  const pieces: TowerScenePiece[] = [];
  let idx = 0;
  for (const placement of placements) {
    const part = partById(sceneParts, placement.partId);
    if (!part) continue;
    const kind = normalizePartKind(part.kind);
    pieces.push({
      instanceKey: `scene-${idx++}-${part.id}`,
      imageDataUrl: part.imageDataUrl,
      gridX: placement.gridX,
      gridY: placement.gridY,
      cellsW: part.cellsW,
      cellsH: part.cellsH,
      kind,
      depth: placementDepth(
        kind,
        placement.gridY,
        part.cellsH,
        placement.depthBias ?? part.depthBias ?? 0,
      ),
    });
  }
  return pieces;
}

export function buildDungeonFloorBattlePlan(
  snapshot: DungeonFloorPublishSnapshot,
): TowerFloorBattlePlan {
  const f = snapshot.floor;
  const powerMultiplier = snapshot.powerMultiplier ?? 1;
  const scenePieces = buildScenePieces(snapshot.scenePlacements ?? [], snapshot.sceneParts);
  const spawns: TowerEnemySpawn[] = [];
  let idx = 0;

  for (const row of snapshot.spawns) {
    const part = partById(snapshot.monsterParts, row.monsterId);
    if (!part) continue;
    const baseCombat = monsterCombatFromPart(part);
    if (!baseCombat) continue;
    const rowPower = row.powerMultiplier ?? 1;
    const combat = scaleMonsterCombat(baseCombat, powerMultiplier * rowPower);
    const ppm = row.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE;
    for (let i = 0; i < row.count; i += 1) {
      spawns.push({
        instanceKey: `${part.id}-${idx++}`,
        monsterId: part.id,
        name: part.name,
        imageDataUrl: part.imageDataUrl,
        spritesheetDataUrl: part.spritesheetDataUrl,
        grid: part.grid,
        cellsH: part.cellsH,
        maxHp: combat.maxHp,
        xpReward: combat.xpReward,
        ratings: combat.ratings,
        spawnPerMinute: ppm,
      });
    }
  }

  if (spawns.length === 0) {
    spawns.push({
      instanceKey: "fallback-0",
      monsterId: "",
      name: `ด่าน ${f}`,
      imageDataUrl: "",
      cellsH: 1,
      maxHp: 120,
      xpReward: 20,
      ratings: {
        level: f,
        attack: 12,
        defense: 4,
        dodge: 2,
        hit: 50,
        critChance: 0.05,
        critMultiplier: 1.5,
        attackSpeed: 0,
        moveSpeed: 0,
        hpRegenPerSec: 0,
        manaMaxBonus: 0,
      },
    });
  }

  const totalXpReward = spawns.reduce((sum, s) => sum + s.xpReward, 0);
  return {
    floor: f,
    name: `ด่าน ${f}`,
    totalXpReward: Math.max(1, totalXpReward),
    spawns,
    customConfig: true,
    powerMultiplier,
    scenePieces,
    sceneAmbience: resolveTowerArenaAmbience(snapshot.sceneAmbience, snapshot.sceneParts),
  };
}
